import type {
  BatchArgs,
  BatchResponse,
  Entity,
  IOrganizationData,
  IntegrationVendor,
  PresentationShareDetails,
  ShareLink,
  ShareSecurity,
  ShareSourceType,
  TourAspectRatio,
  TourShareDetails,
  WelcomeMatConfig,
} from '@carboncopyco/types';
import { Debug, removeUndefinedValues } from '@carboncopyco/utils';
import type { GetFiltersResponse } from '../../types';
import { handleBatchRequest } from '../utils';
import { mainApi } from './mainApi';

Debug.extend('demostack-data:api:shareApi');

export interface GetSharesArgs extends BatchArgs {
  organizationId: string;
  sourceType: ShareSourceType;
  id: number;
  workspaceId?: string;
}

export interface GetShareDetailsArgs {
  organizationId: string;

  token: string;
}
export type GetShareDetailsResponse =
  | PresentationShareDetails
  | TourShareDetails;

export interface GetActiveSharesArgs extends BatchArgs {
  organizationId: string;
  workspaceId?: string;
  folderIds?: string[];
}

export interface CreateShareArgs {
  organizationId: string;
  sourceType: ShareSourceType;
  id: number;
  linkDescription: string;
  skipWelcome: boolean;
  cover?: WelcomeMatConfig;
  expiresAt: number | null;
  daysToExpire: number | null;
  security: ShareSecurity;
  autoSyncUpdates?: boolean;
  tourAspectRatio?: TourAspectRatio;
  integrationLeads?: IntegrationVendor[];
  showGuidanceTooltip?: boolean;
  workspaceId: string;
  welcomeMatTitle: string;
}
export interface EditShareArgs {
  organizationId: string;
  expiresAt: number | null;
  daysToExpire: number | null;
  linkDescription: string;
  security: ShareSecurity;
  shareId: string | number;
  skipWelcome: boolean;
  cover?: WelcomeMatConfig;
  autoSyncUpdates?: boolean;
  tourAspectRatio?: TourAspectRatio;
  integrationLeads?: IntegrationVendor[];
  showGuidanceTooltip?: boolean;
  welcomeMatTitle: string;
}

export interface ToggleShareLinkArgs {
  organizationId: string;
  active: boolean;
  id: number;
}

export interface GetShareFiltersArgs {
  workspaces: string[];
  organizationId: IOrganizationData['id'];
  entityType: Extract<Entity, 'demo' | 'tour'>;
  entityId: number;
  deletedEntities?: boolean;
  query: string;
}

export const shareApi = mainApi.injectEndpoints({
  endpoints: (builder) => ({
    toggleShare: builder.mutation<ShareLink, ToggleShareLinkArgs>({
      query: ({ organizationId, id, active }) => ({
        url: `/api/${organizationId}/share/${id}/toggle-active`,
        method: 'POST',
        body: { active },
      }),
      invalidatesTags: ['Shares'],
    }),

    getShares: builder.query<BatchResponse<ShareLink>, GetSharesArgs>({
      query: ({ organizationId, sourceType, id, ...batchArgs }) =>
        handleBatchRequest({
          ...batchArgs,
          url: `/api/${organizationId}/share/${sourceType}/${id}`,
        }),
      providesTags: (_, __, arg) => [{ type: 'Shares', id: arg.id }],
    }),

    getActiveShares: builder.query<
      BatchResponse<ShareLink>,
      GetActiveSharesArgs
    >({
      query: ({
        organizationId,
        workspaceId,
        folderIds,
        limit,
        ...batchArgs
      }) =>
        handleBatchRequest({
          ...batchArgs,
          url: `/api/${organizationId}/shares/active`,
          params: removeUndefinedValues({
            workspaces: workspaceId,
            folderIds: folderIds?.join(','),
            limit,
          }),
        }),
    }),

    createShare: builder.mutation<ShareLink, CreateShareArgs>({
      query: ({
        organizationId,
        sourceType,
        id,
        linkDescription,
        skipWelcome,
        cover,
        expiresAt,
        daysToExpire,
        security,
        autoSyncUpdates,
        tourAspectRatio,
        integrationLeads,
        showGuidanceTooltip,
        workspaceId,
        welcomeMatTitle,
      }) => ({
        url: `/api/${organizationId}/${sourceType}/${id}/share/link`,
        method: 'PUT',
        body: {
          workspaceId,
          options: {
            linkDescription,
            skipWelcome,
            welcomeMatConfig: cover,
            expiresAt,
            daysToExpire,
            security,
            autoSyncUpdates,
            tourAspectRatio,
            integrations: integrationLeads,
            slackCheckbox: true,
            showGuidanceTooltip,
            welcomeMatTitle,
          },
        },
      }),
      invalidatesTags: (res) =>
        res
          ? [
              { type: 'Shares' },
              {
                type: res.folderId ? 'FolderAssets' : 'WorkspaceAssets',
                id: res.folderId || res.workspaceId,
              },
            ]
          : [{ type: 'Shares' }],
    }),

    editShare: builder.mutation<ShareLink, EditShareArgs>({
      query: ({
        organizationId,
        shareId,
        linkDescription,
        skipWelcome,
        cover,
        expiresAt,
        daysToExpire,
        security,
        autoSyncUpdates,
        tourAspectRatio,
        integrationLeads,
        showGuidanceTooltip,
        welcomeMatTitle,
      }) => ({
        url: `/api/${organizationId}/share/${shareId}`,
        method: 'POST',
        body: {
          options: {
            linkDescription,
            skipWelcome,
            expiresAt,
            daysToExpire,
            security,
            welcomeMatConfig: cover,
            autoSyncUpdates,
            tourAspectRatio,
            integrations: integrationLeads,
            slackCheckbox: true,
            showGuidanceTooltip,
            welcomeMatTitle,
          },
        },
      }),
      invalidatesTags: () => [{ type: 'Shares' }],
    }),

    getShareDetails: builder.query<
      GetShareDetailsResponse,
      GetShareDetailsArgs
    >({
      query: ({ organizationId, token }) => ({
        url: `/api/${organizationId}/share/${token}`,
        method: 'GET',
      }),
    }),

    getShareFilters: builder.mutation<GetFiltersResponse, GetShareFiltersArgs>({
      query: ({
        workspaces,
        organizationId,
        entityType,
        entityId,
        deletedEntities,
        query,
      }) => ({
        url: `/api/${organizationId}/share/${entityType}/${entityId}/search/filters`,
        method: 'POST',
        params: {
          deletedEntities,
          query,
          workspaces: workspaces.join(','),
        },
      }),
    }),
  }),
});

export const {
  useToggleShareMutation,
  useGetSharesQuery,
  useLazyGetSharesQuery,
  useCreateShareMutation,
  useEditShareMutation,
  useGetShareDetailsQuery,
  useGetActiveSharesQuery,
  useLazyGetActiveSharesQuery,
  useGetShareFiltersMutation,
} = shareApi;
